/**
 * AIDiscoveryChat — Floating AI Discovery Chatbot
 *
 * A sales-development chatbot powered by the /api/chat Netlify function
 * (Gemini 1.5 Flash). Guides visitors through structured discovery questions,
 * captures lead info, and submits to Zoho CRM.
 *
 * Usage: Mount once in the root layout — it renders a floating button
 * and a slide-up chat panel.
 */
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaPaperPlane, FaRobot, FaTimes } from 'react-icons/fa/index.js';
import { MdMinimize } from 'react-icons/md/index.js';

const MotionBox = motion(Box);

// ─── Types ────────────────────────────────────────────────────────────────────

interface Message {
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
}

interface CapturedLead {
  name?: string;
  email?: string;
  company?: string;
  conversationSummary?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const API_ENDPOINT = '/.netlify/functions/chat';

const GREETING = `Hi! I'm the TenK Solutions AI advisor. I can help you figure out whether we're the right fit — and where to start.

What's the biggest operational headache your team deals with every week?`;

// Simple heuristic: detect if the model has captured contact info
function extractLeadData(messages: Message[]): CapturedLead | null {
  const fullText = messages.map((m) => m.text).join('\n');

  // Look for email pattern in the conversation
  const emailMatch = fullText.match(/[\w.-]+@[\w.-]+\.[a-z]{2,}/i);
  if (!emailMatch) return null;

  // Build a short summary from user messages only
  const userMessages = messages
    .filter((m) => m.role === 'user')
    .map((m) => m.text)
    .slice(0, 5)
    .join(' | ');

  return {
    email: emailMatch[0],
    conversationSummary: `AI Discovery Chat — ${new Date().toLocaleDateString()}. User said: ${userMessages}`,
  };
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

const ChatBubble = ({
  message,
  isUser,
}: {
  message: Message;
  isUser: boolean;
}) => {
  const userBg = 'blue.500';
  const aiBg = useColorModeValue('gray.100', 'gray.700');
  const aiColor = useColorModeValue('gray.800', 'white');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 } as any}
      display="flex"
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      mb={2}
    >
      {!isUser && (
        <Box
          w={7}
          h={7}
          borderRadius="full"
          bg="blue.500"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr={2}
          flexShrink={0}
          mt={0.5}
        >
          <Icon as={FaRobot} color="white" boxSize={3.5} />
        </Box>
      )}
      <Box
        maxW="78%"
        bg={isUser ? userBg : aiBg}
        color={isUser ? 'white' : aiColor}
        px={4}
        py={3}
        borderRadius={isUser ? '2xl 2xl 0 2xl' : '2xl 2xl 2xl 0'}
        fontSize="sm"
        lineHeight="1.6"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        {message.text}
      </Box>
    </MotionBox>
  );
};

const TypingIndicator = () => {
  const bg = useColorModeValue('gray.100', 'gray.700');
  return (
    <Flex align="center" mb={2}>
      <Box
        w={7} h={7} borderRadius="full" bg="blue.500"
        display="flex" alignItems="center" justifyContent="center"
        mr={2} flexShrink={0}
      >
        <Icon as={FaRobot} color="white" boxSize={3.5} />
      </Box>
      <Box bg={bg} px={4} py={3} borderRadius="2xl 2xl 2xl 0">
        <HStack spacing={1}>
          {[0, 1, 2].map((i) => (
            <MotionBox
              key={i}
              w={1.5} h={1.5} borderRadius="full" bg="gray.400"
              animate={{ y: ['0px', '-4px', '0px'] }}
              transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity } as any}
            />
          ))}
        </HStack>
      </Box>
    </Flex>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AIDiscoveryChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'model', text: GREETING, timestamp: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [unreadCount, setUnreadCount] = useState(1);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Theming
  const panelBg = useColorModeValue('white', 'gray.800');
  const headerBg = 'blue.600';
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.900');

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isMinimized]);

  // Reset unread when opened
  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMsg: Message = { role: 'user', text, timestamp: new Date() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    // Build Gemini-format history (all prior messages)
    const geminiHistory = updatedMessages.map((m) => ({
      role: m.role,
      parts: [{ text: m.text }],
    }));

    // Check if we've just captured a lead
    const lead = !leadCaptured ? extractLeadData(updatedMessages) : null;

    try {
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: geminiHistory,
          ...(lead ? { leadData: lead } : {}),
        }),
      });

      const data = await res.json();
      const reply = data.reply || "I'm having a moment. Please try again or email us at hello@tenksolutions.com.";

      setMessages((prev) => [
        ...prev,
        { role: 'model', text: reply, timestamp: new Date() },
      ]);

      if (lead) {
        setLeadCaptured(true);
      }

      if (!isOpen) {
        setUnreadCount((c) => c + 1);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: "I'm having trouble connecting right now. Please email us directly at hello@tenksolutions.com.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, leadCaptured, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(0);
  };

  return (
    <>
      {/* Floating trigger button */}
      <AnimatePresence>
        {!isOpen && (
          <MotionBox
            position="fixed"
            bottom={{ base: 4, md: 6 }}
            right={{ base: 4, md: 6 }}
            zIndex={1000}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 } as any}
          >
            <Button
              onClick={handleOpen}
              bg="blue.600"
              color="white"
              borderRadius="full"
              h={14}
              px={5}
              boxShadow="0 8px 32px rgba(37,99,235,0.45)"
              _hover={{ bg: 'blue.500', transform: 'translateY(-2px)', boxShadow: '0 12px 40px rgba(37,99,235,0.55)' }}
              transition="all 0.2s"
              leftIcon={<Icon as={FaRobot} boxSize={5} />}
              position="relative"
            >
              <Text fontWeight="bold" fontSize="sm">Ask Our AI Advisor</Text>
              {unreadCount > 0 && (
                <Badge
                  position="absolute"
                  top={-1}
                  right={-1}
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  fontSize="xs"
                  minW={5}
                  h={5}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {unreadCount}
                </Badge>
              )}
            </Button>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            position="fixed"
            bottom={{ base: 0, md: 6 }}
            right={{ base: 0, md: 6 }}
            w={{ base: 'full', md: '380px' }}
            zIndex={1000}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 280, damping: 28 } as any}
            borderRadius={{ base: 0, md: '2xl' }}
            overflow="hidden"
            boxShadow="0 24px 80px rgba(0,0,0,0.25)"
            border="1px solid"
            borderColor={borderColor}
          >
            {/* Header */}
            <Box bg={headerBg} px={4} py={3}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Box
                    w={8} h={8} borderRadius="full" bg="whiteAlpha.300"
                    display="flex" alignItems="center" justifyContent="center"
                  >
                    <Icon as={FaRobot} color="white" boxSize={4} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="white" lineHeight="1.2">
                      TenK AI Advisor
                    </Text>
                    <HStack spacing={1}>
                      <Box w={1.5} h={1.5} borderRadius="full" bg="green.400" />
                      <Text fontSize="2xs" color="whiteAlpha.800">Online · Typically replies instantly</Text>
                    </HStack>
                  </Box>
                </HStack>
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Minimize"
                    icon={<Icon as={MdMinimize} />}
                    size="sm"
                    variant="ghost"
                    color="whiteAlpha.700"
                    _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                    onClick={() => { setIsMinimized(!isMinimized); }}
                  />
                  <IconButton
                    aria-label="Close chat"
                    icon={<Icon as={FaTimes} />}
                    size="sm"
                    variant="ghost"
                    color="whiteAlpha.700"
                    _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                    onClick={() => setIsOpen(false)}
                  />
                </HStack>
              </HStack>
            </Box>

            {/* Body (collapsible) */}
            <AnimatePresence initial={false}>
              {!isMinimized && (
                <MotionBox
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  overflow="hidden"
                >
                  {/* Messages */}
                  <Box
                    bg={panelBg}
                    h="340px"
                    overflowY="auto"
                    p={4}
                    sx={{
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-track': { bg: 'transparent' },
                      '&::-webkit-scrollbar-thumb': { bg: 'gray.300', borderRadius: '2px' },
                    }}
                  >
                    {messages.map((msg, i) => (
                      <ChatBubble
                        key={i}
                        message={msg}
                        isUser={msg.role === 'user'}
                      />
                    ))}
                    {isLoading && <TypingIndicator />}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Lead captured success banner */}
                  {leadCaptured && (
                    <Box bg="green.50" borderTop="1px solid" borderColor="green.200" px={4} py={2}>
                      <Text fontSize="xs" color="green.700" fontWeight="medium">
                        ✅ Your info has been sent. A TenK advisor will reach out within 1 business day.
                      </Text>
                    </Box>
                  )}

                  {/* Input */}
                  <Box
                    bg={panelBg}
                    borderTop="1px solid"
                    borderColor={borderColor}
                    p={3}
                  >
                    <HStack spacing={2}>
                      <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your message…"
                        bg={inputBg}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="full"
                        fontSize="sm"
                        px={4}
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
                        disabled={isLoading}
                      />
                      <IconButton
                        aria-label="Send message"
                        icon={isLoading ? <Spinner size="sm" /> : <Icon as={FaPaperPlane} />}
                        colorScheme="blue"
                        borderRadius="full"
                        size="md"
                        onClick={sendMessage}
                        isDisabled={!input.trim() || isLoading}
                        flexShrink={0}
                      />
                    </HStack>
                    <Text fontSize="2xs" color="gray.400" mt={2} textAlign="center">
                      Powered by TenK Solutions AI · Your info stays private
                    </Text>
                  </Box>
                </MotionBox>
              )}
            </AnimatePresence>
          </MotionBox>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIDiscoveryChat;
